import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { FileDown, FileText, RefreshCcw, X } from "lucide-react";

import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { documentsAPI } from "../services/api";

const pdfFromBase64 = (base64) => {
  if (!base64) return null;
  const binary = window.atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: "application/pdf" });
  return URL.createObjectURL(blob);
};

const EmployeeDocumentModal = ({
  open,
  onClose,
  templates,
  selectedEmployeeIds,
  employeesMap,
  filtersSnapshot,
}) => {
  const { t } = useTranslation();
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFilename, setPreviewFilename] = useState("");
  const [message, setMessage] = useState("");
  const [customFileName, setCustomFileName] = useState("");
  const [generating, setGenerating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const firstEmployeeId = selectedEmployeeIds?.[0] || null;

  useEffect(() => {
    if (!open) {
      setSelectedTemplateId(null);
      setPreviewUrl((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
        return null;
      });
      setPreviewFilename("");
      setMessage("");
      setCustomFileName("");
      setPreviewLoading(false);
      setGenerating(false);
    }
  }, [open, refreshKey]);

  useEffect(() => {
    if (!open || !selectedTemplateId || !firstEmployeeId) {
      return () => {};
    }
    let cancelled = false;
    const loadPreview = async () => {
      try {
        setPreviewLoading(true);
        const payload = {
          template_id: Number(selectedTemplateId),
          employee_id: firstEmployeeId,
          overrides: { customMessage: message },
        };
        const response = await documentsAPI.previewDocument(payload);
        if (!cancelled) {
          setPreviewFilename(response.filename);
          setPreviewUrl((url) => {
            if (url) {
              URL.revokeObjectURL(url);
            }
            return pdfFromBase64(response.pdf_base64);
          });
        }
      } catch (error) {
        toast.error(t("employeeDocuments.toasts.previewError"));
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    };
    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [open, selectedTemplateId, firstEmployeeId, message, refreshKey, t]);

  const handleDownload = async () => {
    if (!selectedTemplateId || !selectedEmployeeIds?.length) {
      toast.warning(t("employeeDocuments.toasts.selectionRequired"));
      return;
    }
    setGenerating(true);
    try {
      const response = await documentsAPI.generateDocuments({
        template_id: Number(selectedTemplateId),
        employee_ids: selectedEmployeeIds,
        overrides: { customMessage: message },
        file_name: customFileName ? `${customFileName}.pdf` : undefined,
        filters_snapshot: filtersSnapshot || undefined,
        custom_css: null,
        custom_html: null,
      });

      const downloadUrl = pdfFromBase64(response.pdf_base64);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = response.filename;
      link.click();
      URL.revokeObjectURL(downloadUrl);
      toast.success(t("employeeDocuments.toasts.generated"));
      onClose();
    } catch (error) {
      toast.error(t("employeeDocuments.toasts.generateError"));
    } finally {
      setGenerating(false);
    }
  };

  const handleClose = () => {
    onClose();
    setRefreshKey((key) => key + 1);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-primary" />
            <span>{t("employeeDocuments.title")}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 overflow-y-auto pr-1">
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase text-muted-foreground">{t("employeeDocuments.template")}</Label>
              <Select value={selectedTemplateId?.toString() || ""} onValueChange={setSelectedTemplateId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("employeeDocuments.selectTemplate")} />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id.toString()}>
                      <div className="flex flex-col">
                        <span className="font-medium">{template.name}</span>
                        {template.description && (
                          <span className="text-xs text-muted-foreground">{template.description}</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase text-muted-foreground">{t("employeeDocuments.fileName")}</Label>
              <Input
                placeholder={t("employeeDocuments.fileNamePlaceholder")}
                value={customFileName}
                onChange={(e) => setCustomFileName(e.target.value)}
              />
            </div>

            <div>
              <Label className="text-xs uppercase text-muted-foreground">{t("employeeDocuments.extraMessage")}</Label>
              <Textarea
                placeholder={t("employeeDocuments.extraMessagePlaceholder")}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
              />
            </div>

            <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-2">
              <p className="font-semibold">{t("employeeDocuments.summary")}</p>
              <p>
                <span className="text-muted-foreground">{t("employeeDocuments.selectedEmployees")}</span>{" "}
                <strong>{selectedEmployeeIds?.length || 0}</strong>
              </p>
              {selectedEmployeeIds?.length > 0 && (
                <p className="text-muted-foreground">
                  {t("employeeDocuments.firstEmployee", { name: employeesMap?.[selectedEmployeeIds[0]]?.full_name || "—" })}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase text-muted-foreground">{t("common.actions")}</Label>
              <div className="flex flex-col space-y-2">
                <Button variant="outline" className="justify-start" disabled={!selectedTemplateId} onClick={() => setRefreshKey((key) => key + 1)}>
                  <RefreshCcw className="w-4 h-4 mr-2" />
                  {t("employeeDocuments.regeneratePreview")}
                </Button>
                <Button variant="outline" className="justify-start" onClick={handleClose}>
                  <X className="w-4 h-4 mr-2" />
                  {t("common.cancel")}
                </Button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 border rounded-lg overflow-hidden bg-muted/50">
            <div className="border-b px-4 py-2 flex items-center justify-between bg-card">
              <span className="font-medium">{t("employeeDocuments.previewTitle")}</span>
              {previewFilename && <span className="text-xs text-muted-foreground">{previewFilename}</span>}
            </div>
            <div className="h-full">
              {previewLoading ? (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  {t("employeeDocuments.generatingPreview")}
                </div>
              ) : previewUrl ? (
                <iframe title={t("employeeDocuments.previewFrameTitle")} src={previewUrl} className="w-full h-full" />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground px-4 text-center">
                  {t("employeeDocuments.previewHint")}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="ghost" onClick={handleClose}>
            {t("common.close")}
          </Button>
          <Button
            onClick={handleDownload}
            disabled={!selectedTemplateId || !selectedEmployeeIds?.length || generating}
          >
            <FileDown className="w-4 h-4 mr-2" />
            {generating ? t("employeeDocuments.generating") : t("employeeDocuments.downloadPdf")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeDocumentModal;
